/**
 * Transcript Feature
 * Includes: enable/disable toggle,
 * language select, search form, and download
 */

import { h } from "preact";
import { useEffect, useState, useMemo, useContext } from "preact/hooks";
import { forwardRef } from "preact/compat";

import { Paragraph, TranscriptResponse } from "../types/transcriptApiTypes";

// helpers
import { fetchData } from "../../shared";
import findSearchMatches from "../helpers/findSearchMatches";
import sortLangs from "../helpers/sortLangs";
import preferredLangs from "../helpers/preferredLangs";

// components
import Results from "./Results";
import SearchForm from "./SearchForm";
import TranscriptToolbar from "./TranscriptToolbar";
import { FastTranscriptBody } from "./FastTranscriptBody";

// context
import SearchContext from "../../features/utils/SearchContext";

import { recordedEndpoint, useRecordedURL } from "../../helpers/routes";
import SettingsContext from "../../SettingsContext";
import { Footer } from "../../shared/components";
import { AvailableLanguages } from "../../../types/rootDataTypes";

interface TranscriptProps {
  tranLangs: AvailableLanguages;
  onWordClick: (milliseconds: number) => void;
  ref: HTMLDivElement;
}

const Transcript = forwardRef<HTMLDivElement, TranscriptProps>((props, ref) => {
  const { onWordClick, tranLangs } = props;

  const sortedLangs = useMemo(
    () => sortLangs(tranLangs, preferredLangs),
    [tranLangs, preferredLangs]
  );

  const [selectedLang, setSelectedLang] = useState(
    sortedLangs[0]?.browser_navigator_code
  );

  const [transcriptEnabled, setTranscriptEnabled] = useState<boolean>(true);
  const [paragraphs, setParagraphs] = useState<Paragraph[]>([]);

  const { settings } = useContext(SettingsContext);
  const { searchString } = useContext(SearchContext);

  const searchResults = useMemo(
    () => (searchString ? findSearchMatches(paragraphs, searchString) : []),
    [paragraphs, searchString]
  );

  const getData = async (url: string) => {
    const { data, error } = await fetchData<TranscriptResponse>(url);
    // TODO better error handling

    if (!error && data) {
      setParagraphs(data.data);
    }
  };

  const transcriptURL = useRecordedURL("/transcript", {
    format: "tx-json-ap",
    preferred_languages: preferredLangs,
  }).toString();

  useEffect(() => {
    getData(transcriptURL);
  }, [transcriptURL]);

  let transcriptTextClasses = "Transcript-text";

  if (!transcriptEnabled) {
    transcriptTextClasses += ` Transcript-text-isHidden`;
  }

  transcriptTextClasses += ` ${
    settings.inIframe
      ? "Transcript-iframeTranscriptHeight"
      : "Transcript-directEmbedTranscriptHeight"
  }`;

  return (
    <div ref={ref} class="Transcript-container">
      <TranscriptToolbar
        toggled={transcriptEnabled}
        onToggle={setTranscriptEnabled}
        showToggle={!settings.inIframe}
        showLanguageDropdown={settings.showLanguageDropdown}
        sortedLangs={sortedLangs}
        langChange={(value: string) => {
          setSelectedLang(value);

          getData(
            recordedEndpoint(settings, "/transcript", {
              format: "tx-json-ap",
              preferred_languages: [value],
            }).toString()
          );
        }}
      />
      <div
        class={
          transcriptEnabled ? "Transcript-wrapper" : "Transcript-hiddenWrapper"
        }
      >
        <SearchForm
          id="keyword-search"
          enabled={transcriptEnabled}
          selectedLang={selectedLang}
        />
        {/* Announce results to screen readers */}
        <div role="status" aria-live="polite">
          <Results
            searchResults={searchResults}
            searchString={searchString}
            enabled={transcriptEnabled}
          />
        </div>
        <div
          class={transcriptTextClasses}
          role="document"
          data-testid="transcript"
          tabIndex={0}
          dir="auto"
          lang={selectedLang}
        >
          <FastTranscriptBody
            paragraphs={paragraphs}
            onWordClick={onWordClick}
            searchResults={searchResults}
          />
        </div>
      </div>

      {transcriptEnabled && <Footer />}
    </div>
  );
});

export default Transcript;
